import { Cart } from "./cart";
import { CartItem, RestaurantCartItem, RestaurantCartValue } from "./types";
import { DayPart } from "@/dto/dayPartMenu";
import { DateTime } from "luxon";
import { HHMMtoDate } from "@/utils/dateTimeUtils";
import { ModifierGroup } from "@/dto/modifierGroup";
import { Modifier } from "@/dto/modifier";
import { CartItemType, CartType } from "@/constants/enum";
import { v4 as uuidv4 } from "uuid";
import { LocalStorageKey } from "@/utils/storageKeys";

/**
 * Cart implementation specifically for the Restaurant vertical.
 *
 * Handles logic unique to food ordering, such as special instructions and
 * scheduled pickup/delivery times.
 *
 * @extends {Cart<RestaurantCartItem>}
 */
export class RestaurantCart extends Cart<RestaurantCartItem> {
  /** Global special instructions for the entire order (e.g., "Napkins please"). */
  public specialInstructions: string;
  /** The scheduled time for the order, or null for ASAP. */
  public scheduledTime: Date | null; // For scheduled orders

  /**
   * Creates a new RestaurantCart instance.
   *
   * @param data - Optional initial data.
   */
  constructor(data?: any) {
    super(data);
    this.specialInstructions = data?.specialInstructions || "";
    this.scheduledTime = data?.scheduledTime
      ? new Date(data.scheduledTime)
      : null;
  }

  // Override toJSON to include the type discriminator and specific properties
  toJSON(): any {
    return {
      ...super.toJSON(),
      specialInstructions: this.specialInstructions,
      scheduledTime: this.scheduledTime?.toISOString() || null,
      type: CartType.RESTAURANT_CART,
    };
  }

  copyPropertiesFrom(other: Cart): void {
    super.copyPropertiesFrom(other);
    if (other instanceof RestaurantCart) {
      this.specialInstructions = other.specialInstructions;
      this.scheduledTime = other.scheduledTime;
    }
  }

  // Static method to re-hydrate from a plain object
  static fromJSON(data: any): RestaurantCart {
    return new RestaurantCart(data);
  }

  static addItem(item: RestaurantCartValue): void {
    const cartItem: CartItem = {
      key: item.cartId,
      value: item,
    } as CartItem;
    this._addToStorage(LocalStorageKey.RESTAURANT_CART, cartItem);
  }

  static getItems(): RestaurantCartItem[] {
    return this._getFromStorage<RestaurantCartItem>(
      LocalStorageKey.RESTAURANT_CART
    );
  }

  static clear(): void {
    this._clearStorage(LocalStorageKey.RESTAURANT_CART);
  }

  static removeItem(cartId: string): void {
    this._removeFromStorage(LocalStorageKey.RESTAURANT_CART, cartId);
  }

  static isCurrentTimePastDayPartTime(
    cartItems: RestaurantCartItem[],
    isAsap: boolean = false,
    daypartEndTime: Number,
    timezone: string
  ): boolean {
    if (cartItems?.length > 0 && isAsap && daypartEndTime) {
      const nowInMerchantZone = DateTime.now().setZone(timezone);
      const menuEndTime = HHMMtoDate(
        daypartEndTime.toString().padStart(4, "0"),
        timezone,
        new Date()
      );
      if (nowInMerchantZone > menuEndTime) {
        return true;
      }
    }
    return false;
  }

  static getRestaurantCartItemsStatus(
    cartItems: RestaurantCartItem[],
    isAsap: boolean,
    dayPart: DayPart,
    timezone: string
  ): "past" | "inactive" | "active" {
    /**
     * If ASAP order and daypartmenu is selected, check if current time is past the daypart menu end time. If yes, return "past"
     */
    let isPast = RestaurantCart.isCurrentTimePastDayPartTime(
      cartItems,
      isAsap,
      dayPart?.time?.endTime,
      timezone
    );

    if (isPast) return "past";

    /**
     * Check if there are any items that dont belong to current chosen daypart menu. If yes, return "Inactive"
     */
    if (
      cartItems.some(
        (item: RestaurantCartItem) => item.value.menuId !== dayPart?.menuId
      )
    ) {
      return "inactive";
    }

    return "active";
  }

  static buildRestaurantCartItemPayload(
    type: CartItemType.RESTAURANT,
    item: RestaurantCartValue,
    quantity: number,
    selectedModifiers: any,
    selectableModifierGroups: ModifierGroup[],
    allModifiers: Modifier[],
    specialInstructions: string,
    totalPrice: number,
    menuId?: string,
    existingCartId?: string
  ) {
    const allItemModifiers: Modifier[] = [];

    Object.values(selectableModifierGroups).forEach((group: ModifierGroup) => {
      const groupSelections = selectedModifiers[group.id];
      if (groupSelections) {
        Object.keys(groupSelections).forEach((modifierId) => {
          const modifier = allModifiers.find((m) => m.id === modifierId);
          if (modifier) {
            allItemModifiers.push({
              ...modifier,
              quantity: groupSelections[modifierId],
            });
          }
        });
      }
    });

    return {
      type: type,
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: quantity,
      modifiers: allItemModifiers,
      specialInstructions: specialInstructions,
      subtotal: totalPrice,
      total: totalPrice,
      isAgeRestricted: item.isAgeRestricted,
      menuId: menuId,
      cartId: existingCartId || uuidv4(),
    };
  }
}
